import { getApiBase } from "@/lib/api-public"

export interface RunCyclePayload {
  symbol: string
  timeframe?: string
  bars?: number
  execute_now?: boolean
  execute_in_minutes?: number | null
}

export interface BackendPortfolio {
  as_of?: string
  as_of_utc?: string
  as_of_local?: string
  timezone_note?: string
  equity?: number
  balance?: number
  profit?: number
  margin?: number
  free_margin?: number
  margin_level?: number
  currency?: string
  leverage?: number
  positions?: Array<Record<string, unknown>>
  /** When present (live MT5 snapshot), preferred over `positions` for open rows. */
  open_positions?: Array<Record<string, unknown>>
  source?: string
  stale?: boolean
}

export interface RunCycleResponse {
  portfolio?: BackendPortfolio
  market_context?: {
    articles?: number
    news_score?: number
  }
  proposal?: {
    source_agent?: string
    symbol?: string
    direction?: "buy" | "sell"
    entry?: number
    stop_loss?: number
    take_profit?: number
    confidence?: number
    reasoning?: string
    lot?: number
    edge_id?: string
    edge_primary?: EdgeMatchView
    edge_matches?: EdgeMatchView[]
    edge_throttle?: {
      active?: boolean
      blocked?: boolean
      reasons?: string[]
      size_multiplier_applied?: number
      confidence_delta_applied?: number
    }
  } | null
  validation?: {
    approved?: boolean
    reasons?: string[]
  }
  execution_result?: unknown
  scheduled_trade?: unknown
  requested_symbol?: string
  resolved_symbol?: string
  symbol_evaluations?: Array<{
    requested_symbol?: string
    resolved_symbol?: string
    status?: string
    rejection_reason?: string
    rejection_detail?: string
    combined_score?: number
    combined_score_floor?: number
    trade_quality_score?: number
    trade_quality_components?: Record<string, number>
    memory_feedback?: {
      lookback?: number
      pattern_win_rate?: number
      pattern_sample?: number
      last_10_net_pnl?: number
      last_5_outcomes?: number[]
      same_setup_recent_losses?: number
      confidence_delta?: number
      risk_multiplier?: number
      pause_symbol?: boolean
      pause_reason?: string | null
    }
    tech_quality?: {
      vol_regime?: string
      strategy_mode?: string
      trend_strength_atr?: number
      atr_percentile?: number
      confidence_effective?: number
    }
  }>
  agent_trace?: Array<{
    timestamp: string
    agent: string
    stage: string
    status: string
    response: unknown
  }>
}

export interface TransparencyRecord {
  requested_symbol: string
  resolved_symbol?: string
  selected_symbol?: string
  /** ISO timestamp of when this cycle's row was written to SQLite (from _db_created_at). */
  cycle_recorded_at?: string | null
  precheck?: Record<string, unknown> | null
  technical?: Record<string, unknown> | null
  market?: Record<string, unknown> | null
  portfolio_agent?: Record<string, unknown> | null
  validation?: Record<string, unknown> | null
  execution_result?: unknown
  scheduled_trade?: unknown
  run_cycle_output?: RunCycleResponse | null
  agent_trace?: RunCycleResponse["agent_trace"]
  error?: string
}

export interface TransparencyResponse {
  generated_at: string
  symbols: string[]
  portfolio_snapshot: BackendPortfolio
  records: TransparencyRecord[]
}

export interface UniverseQuote {
  requested_symbol: string
  symbol: string
  bid?: number
  ask?: number
  spread?: number
  /** Daily close-to-close % change from MT5 D1 bars (previous vs last completed day). */
  change_pct?: number | null
  as_of?: string
  status: "ok" | "error"
  error?: string
}

export type AgentFleetHealth = "ok" | "degraded" | "offline"

export type AgentFreshnessVerdict = "fresh" | "stale" | "never_run"

export interface AgentFleetItem {
  agent_key: string
  display_name: string
  description: string
  health: AgentFleetHealth
  health_notes: string[]
  /** Present on fleet snapshots; omitted on older API responses. */
  freshness_verdict?: AgentFreshnessVerdict
  execution_count: number
  last_executed_at: string | null
  has_memory: boolean
  memory_kind: "files" | "sqlite" | "none"
  memory_hint: string
}

export interface FleetNarrative {
  text: string
  updated_at: string
}

export interface AgentFleetResponse {
  generated_at: string
  scheduled_trades_total: number
  agents: AgentFleetItem[]
  narratives?: {
    orchestration_agent: FleetNarrative | null
    execution_agent: FleetNarrative | null
  }
}

export interface AgentDecisionLogDetail {
  last_row_id: number | null
  last_created_at: string | null
  last_symbol: string | null
  total_rows: number
  rows_last_24h: number
  staleness_seconds: number | null
  staleness_threshold_seconds: number
  freshness_verdict: AgentFreshnessVerdict
}

export interface AgentSnapshotAlignment {
  detail_queried_at: string
  fleet_snapshot_generated_at?: string
  db_last_decision_at: string | null
  seconds_since_db_write: number | null
  note: string
}

export interface AgentClientCardAlignment {
  client_last_executed_at: string | null | undefined
  matches_db_last_run: boolean | null
  message: string | null
}

export interface AgentOrchestrationFeedDetail {
  latest_cycle_logged_at: string | null
  latest_cycle_symbol: string | null
  agents_in_latest_trace: string[]
  this_agent_in_latest_trace: boolean
  trace_error_steps: Array<{ timestamp: string; agent: string; stage: string; status: string }>
  recent_trace_steps: Array<{ timestamp: string; agent: string; stage: string; status: string }>
  explain: string
}

export interface AgentHealthAutotradeSlice {
  enabled: boolean
  interval_minutes: number
  last_run_at?: string | null
  last_error?: string | null
}

export interface AgentHealthLlmSlice {
  openai_configured: boolean
  model: string
  hints: string[]
}

export interface AgentRecentDecisionRow {
  id: number
  created_at: string
  symbol: string
  summary: { top_level_keys: string[]; notes: string[] }
}

/** Superset of fleet row returned by GET /agents/{key}/health (live detail read). */
export type AgentHealthDetailResponse = AgentFleetItem & {
  detail_queried_at: string
  decision_log: AgentDecisionLogDetail
  snapshot_alignment: AgentSnapshotAlignment
  client_card_alignment: AgentClientCardAlignment
  orchestration_feed: AgentOrchestrationFeedDetail
  autotrade: AgentHealthAutotradeSlice
  llm: AgentHealthLlmSlice
  recent_decisions: AgentRecentDecisionRow[]
}

export interface PortfolioHistoryPoint {
  recorded_at: string
  equity: number
  balance: number
  margin?: number
  free_margin?: number
}

export interface PortfolioHistoryResponse {
  generated_at: string
  interval_hours: number
  days: number
  points: PortfolioHistoryPoint[]
}

export interface TradeHistoryItem {
  id: number
  created_at: string
  updated_at: string
  opened_at?: string | null
  closed_at?: string | null
  created_at_utc?: string | null
  created_at_local?: string | null
  updated_at_utc?: string | null
  updated_at_local?: string | null
  opened_at_utc?: string | null
  opened_at_local?: string | null
  closed_at_utc?: string | null
  closed_at_local?: string | null
  requested_symbol?: string | null
  symbol: string
  direction: string
  lot: number
  status: string
  scheduled_trade_id?: number | null
  mt5_ticket?: number | null
  entry_price?: number | null
  exit_price?: number | null
  stop_loss?: number | null
  take_profit?: number | null
  realized_pnl?: number | null
  profit_currency?: string | null
  confidence?: number | null
  source_agent?: string | null
  market_signal?: string | null
  news_score?: number | null
  technical_signal?: string | null
  close_reason?: string | null
  execution_payload?: unknown
  extra_context?: unknown
}

export interface TradeHistoryStats {
  total: number
  pending_total: number
  open_total: number
  closed_total: number
  failed_total: number
  wins: number
  losses: number
  breakeven: number
  win_rate: number
  net_pnl: number
  avg_pnl: number
  edge_validation?: Record<string, unknown>
  edge_stats?: EdgeStatsItem[]
}

export interface EdgeMatchView {
  matched: boolean
  edge_id: string
  direction_bias?: string | null
  confidence_delta?: number
  size_multiplier?: number
  invalidators?: string[]
  context?: Record<string, unknown>
}

export interface EdgeStatsItem {
  edge_id: string
  closed_trades: number
  wins: number
  losses: number
  breakeven: number
  net_pnl: number
  expectancy_per_trade: number
  win_rate: number
  profit_factor: number
  consecutive_losses: number
  last_trade_at?: string | null
  updated_at?: string | null
  throttle?: {
    active: boolean
    blocked: boolean
    reasons: string[]
    size_multiplier: number
    confidence_delta: number
  }
}

/** One MT5 history deal leg (in/out); merged into the dashboard ledger when MT5 is connected. */
export interface Mt5DealLedgerItem {
  row_type: "mt5_deal"
  deal_ticket: number
  order_ticket: number
  position_id: number
  time: string
  symbol: string
  side: "buy" | "sell"
  entry_leg: "in" | "out" | "inout" | "other"
  volume: number
  price: number
  profit: number
  commission: number
  swap: number
  fee: number
  pnl_leg: number
  magic: number
  comment?: string | null
  is_agent_deal: boolean
  approx_notional?: number | null
  notional_hint?: string | null
}

export interface TradeHistoryResponse {
  generated_at: string
  stats: TradeHistoryStats
  items: TradeHistoryItem[]
  /** Raw MT5 deal legs (last ~120 days) when the terminal bridge is available. */
  mt5_deals?: Mt5DealLedgerItem[]
  mt5_deals_note?: string
}

export interface SystemAnalysisPayload {
  agent: string
  summary: string
  what_the_log_is_showing: string[]
  specific_problems_visible: string[]
  good_news_in_this_trace: string[]
}

export interface SystemAnalysisResponse {
  generated_at: string
  source: string
  analysis: SystemAnalysisPayload
}

export interface FxSessionContext {
  utc_hour?: number
  session: string
  session_label: string
  display_name?: string
  active_sessions?: string[]
  is_overlap_london_ny?: boolean
  is_transition?: boolean
  sl_multiplier?: number
}

export interface MaeAnalyticsSummary {
  avg_mae_pips?: number
  avg_sl_pips?: number
  avg_mfe_pips?: number
  avg_entry_efficiency_r?: number | null
  win_rate?: number
  pct_mae_exceeded_sl?: number
  insight?: string | null
}

export interface MaeAnalyticsTradeRow {
  id?: number
  symbol?: string
  direction?: string
  mae_pips?: number
  mfe_pips?: number
  mae_r?: number
  sl_pips?: number
  mae_vs_sl?: number | null
  entry_efficiency_r?: number | null
  entry_efficiency_label?: string | null
  outcome?: string
  fx_session?: string
  session_label?: string
}

export interface MaeAnalyticsSessionRow {
  session: string
  trades: number
  win_rate?: number
  avg_mae_pips?: number | null
  avg_sl_pips?: number | null
  avg_mae_r?: number | null
}

export interface MaeAnalyticsPayload {
  sample_size?: number
  with_mae_data?: number
  summary?: MaeAnalyticsSummary
  by_session?: MaeAnalyticsSessionRow[]
  trades?: MaeAnalyticsTradeRow[]
}

export interface DashboardFeedResponse {
  generated_at: string
  transparency: TransparencyResponse
  trade_history: TradeHistoryResponse
  edge_stats?: EdgeStatsItem[]
  portfolio_history: PortfolioHistoryResponse
  agent_fleet: AgentFleetResponse
  system_analysis: SystemAnalysisResponse
  quotes: { generated_at: string; items: UniverseQuote[] }
  session_context?: FxSessionContext
  mae_analytics?: MaeAnalyticsPayload
}

export interface AutoTradeSettings {
  enabled: boolean
  interval_minutes: number
  timeframe: string
  bars: number
  max_candidates: number
  universe_symbols: string[]
  /** Full list of pairs available for autotrade selection (from API). */
  symbol_universe_options: string[]
  manage_exits: boolean
  last_run_at?: string | null
  last_error?: string | null
  updated_at?: string
}

const API_BASE = getApiBase()

export async function fetchAgentPortfolio(): Promise<BackendPortfolio> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/portfolio`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Portfolio fetch failed (${res.status})`)
  return res.json()
}

export async function fetchLiveMetrics(): Promise<{ source: string; fetched_at: string; portfolio: BackendPortfolio }> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/metrics-live`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Live metrics fetch failed (${res.status})`)
  return res.json()
}

export async function fetchPortfolioHistory(days: number = 730): Promise<PortfolioHistoryResponse> {
  const d = Math.max(1, Math.min(Math.floor(days), 2000))
  const res = await fetch(`${API_BASE}/api/agent-force-1/portfolio-history?days=${d}`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Portfolio history fetch failed (${res.status})`)
  return res.json()
}

export async function runAgentCycle(payload: RunCyclePayload): Promise<RunCycleResponse> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/run-cycle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      symbol: payload.symbol,
      timeframe: payload.timeframe ?? "M15",
      bars: payload.bars ?? 250,
      execute_now: payload.execute_now ?? false,
      execute_in_minutes: payload.execute_in_minutes ?? null,
    }),
  })
  if (!res.ok) throw new Error(`Cycle fetch failed (${res.status})`)
  return res.json()
}

export async function fetchScheduledTrades(): Promise<{ items: Array<Record<string, unknown>> }> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/scheduled-trades`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Scheduled trades fetch failed (${res.status})`)
  return res.json()
}

export async function fetchTradeHistory(limit: number = 300): Promise<TradeHistoryResponse> {
  const n = Math.max(20, Math.min(Math.floor(limit), 2000))
  const res = await fetch(`${API_BASE}/api/agent-force-1/trade-history?limit=${n}`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Trade history fetch failed (${res.status})`)
  return res.json()
}

export async function fetchTransparency(payload: {
  symbols: string[]
  timeframe?: string
  bars?: number
  include_agent_trace?: boolean
  /** When true, runs exit review per open position (heavy). Dashboard keeps this false. */
  manage_exits?: boolean
}): Promise<TransparencyResponse> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/transparency`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      symbols: payload.symbols,
      timeframe: payload.timeframe ?? "M15",
      bars: payload.bars ?? 250,
      include_agent_trace: payload.include_agent_trace ?? true,
      max_symbols: payload.symbols.length,
      manage_exits: payload.manage_exits ?? false,
    }),
  })
  if (!res.ok) throw new Error(`Transparency fetch failed (${res.status})`)
  return res.json()
}

export interface PairSeriesPoint {
  time: string
  open: number
  high: number
  low: number
  close: number
}

export interface PairSeriesResponse {
  requested_symbol: string
  resolved_symbol: string | null
  timeframe: string
  bar_minutes: number
  hours_requested: number
  generated_at: string
  status: "ok" | "error"
  message: string | null
  points: PairSeriesPoint[]
}

/** Market overview keys like EUR/USD → EURUSD for MT5 APIs. */
export function pairDisplayToBackendSymbol(pair: string): string {
  return pair.replace(/\//g, "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6) || "EURUSD"
}

export async function fetchPairSeries(symbolDisplay: string, hours = 5, timeframe?: string): Promise<PairSeriesResponse> {
  const sym = encodeURIComponent(pairDisplayToBackendSymbol(symbolDisplay))
  const tf = timeframe ? `&timeframe=${encodeURIComponent(String(timeframe).toUpperCase())}` : ""
  const q = `symbol=${sym}&hours=${hours}${tf}`
  const base = `${API_BASE}/api/agent-force-1`
  let res = await fetch(`${base}/pair-series?${q}`, { cache: "no-store" })
  if (res.status === 404) {
    res = await fetch(`${base}/pair_series?${q}`, { cache: "no-store" })
  }
  if (!res.ok) throw new Error(`Pair series fetch failed (${res.status})`)
  return res.json()
}

/** Payload from `GET /api/agent-force-1/pair-news` (market agent news + LLM summary). */
export interface PairNewsResponse {
  pair?: string
  articles?: number
  relevant_articles?: number
  news_score?: number
  market_signal?: string
  market_confidence?: number
  headlines?: string[]
  fetched_headlines?: string[]
  llm_analysis?: string
  llm_key_drivers?: string[]
  llm_risk_flags?: string[]
  as_of?: string
  cache_hit?: boolean
  [key: string]: unknown
}

export async function fetchPairNews(symbolDisplay: string): Promise<PairNewsResponse> {
  const sym = encodeURIComponent(pairDisplayToBackendSymbol(symbolDisplay))
  const res = await fetch(`${API_BASE}/api/agent-force-1/pair-news?symbol=${sym}`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Pair news fetch failed (${res.status})`)
  return res.json() as Promise<PairNewsResponse>
}

export async function fetchUniverseQuotes(payload: { symbols: string[] }): Promise<{ generated_at: string; items: UniverseQuote[] }> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/quotes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      symbols: payload.symbols,
      max_symbols: payload.symbols.length,
    }),
  })
  if (!res.ok) throw new Error(`Quotes fetch failed (${res.status})`)
  return res.json()
}

export async function fetchAgentFleet(): Promise<AgentFleetResponse> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/agent-fleet`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Agent fleet fetch failed (${res.status})`)
  return res.json()
}

export async function fetchAutoTradeSettings(): Promise<AutoTradeSettings> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/autotrade-settings`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Auto-trade settings fetch failed (${res.status})`)
  return res.json()
}

export async function updateAutoTradeSettings(
  payload: Partial<
    Pick<
      AutoTradeSettings,
      | "enabled"
      | "interval_minutes"
      | "timeframe"
      | "bars"
      | "max_candidates"
      | "manage_exits"
      | "universe_symbols"
    >
  >,
): Promise<AutoTradeSettings> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/autotrade-settings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`Auto-trade settings update failed (${res.status})`)
  return res.json()
}

export async function fetchAgentHealth(
  agentKey: string,
  options?: { clientCardLastExecutedAt?: string | null },
): Promise<AgentHealthDetailResponse> {
  const key = encodeURIComponent(agentKey)
  const params = new URLSearchParams()
  const cardAt = options?.clientCardLastExecutedAt
  if (cardAt) params.set("client_card_last_executed_at", cardAt)
  const qs = params.toString()
  const res = await fetch(
    `${API_BASE}/api/agent-force-1/agents/${key}/health${qs ? `?${qs}` : ""}`,
    { cache: "no-store" },
  )
  if (!res.ok) throw new Error(`Agent health fetch failed (${res.status})`)
  return res.json()
}

export async function fetchLatestSystemAnalysis(): Promise<SystemAnalysisResponse> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/system-analysis/latest`, { cache: "no-store" })
  if (!res.ok) throw new Error(`System analysis fetch failed (${res.status})`)
  return res.json()
}

export async function runSystemAnalysis(payload: {
  log_text?: string
  run_cycle_output?: Record<string, unknown>
}): Promise<SystemAnalysisResponse> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/system-analysis`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`System analysis run failed (${res.status})`)
  return res.json()
}

export async function fetchDashboardFeed(): Promise<DashboardFeedResponse> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/dashboard-feed`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Dashboard feed fetch failed (${res.status})`)
  return res.json()
}

export async function fetchSessionContext(): Promise<FxSessionContext & { generated_at?: string }> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/session-context`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Session context fetch failed (${res.status})`)
  return res.json()
}

export async function fetchMaeAnalytics(limit = 120): Promise<MaeAnalyticsPayload & { generated_at?: string }> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/mae-analytics?limit=${limit}`, { cache: "no-store" })
  if (!res.ok) throw new Error(`MAE analytics fetch failed (${res.status})`)
  return res.json()
}

/** Download full MAE analytics workbook (all closed trades). */
export async function downloadMaeAnalyticsExcel(): Promise<void> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/mae-analytics/export`, { cache: "no-store" })
  if (!res.ok) {
    let detail = `MAE export failed (${res.status})`
    try {
      const body = (await res.json()) as { detail?: string }
      if (body.detail) detail = body.detail
    } catch {
      /* ignore */
    }
    throw new Error(detail)
  }
  const blob = await res.blob()
  const disposition = res.headers.get("Content-Disposition") ?? ""
  const match = disposition.match(/filename="?([^";\n]+)"?/)
  const filename = match?.[1] ?? `mae_analytics_${new Date().toISOString().slice(0, 10)}.xlsx`
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  anchor.rel = "noopener"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export async function fetchEdgeStats(): Promise<{ generated_at: string; items: EdgeStatsItem[] }> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/edge-stats`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Edge stats fetch failed (${res.status})`)
  return res.json()
}

// ── Cycle progress ────────────────────────────────────────────────────────────

export interface CycleSymbolStatus {
  symbol: string
  status: string
  reason?: string
  score?: number
  is_candidate?: boolean
}

export interface CycleProgressState {
  phase: "idle" | "init" | "exit_management" | "symbol_analysis" | "selection" | "execution" | "done" | "error"
  pct: number
  done: boolean
  symbols_total: number
  symbols: string[]
  symbols_done: number
  symbol_statuses: CycleSymbolStatus[]
  elapsed_ms: number
  started_at: string | null
  result_status: "no_trade" | "trade_selected" | "error" | null
  selected_symbol: string | null
  error: string | null
  updated_at: string | null
  queried_at?: string
}

export async function fetchCycleProgress(): Promise<CycleProgressState> {
  const res = await fetch(`${API_BASE}/api/agent-force-1/run-cycle/latest-progress`, { cache: "no-store" })
  if (!res.ok) throw new Error(`Cycle progress fetch failed (${res.status})`)
  return res.json()
}
